import appSettings from 'wanderu-config';
import {
    fetchCarrierAction,
    carrierDataSelectors as carrierSelectors,
} from 'wanderu-ducks-carriers';
import { liveCheckCachedTripAction } from 'wanderu-ducks-live-check';
import {
    fetchTripAction,
    fetchTripInformationAction,
    tripSelectors,
} from 'wanderu-ducks-trips';
import { languageSelector } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import {
    LIVE_CHECK_TRIP_NOT_FOUND_ERRORS,
    LIVE_CHECK_CACHE_EXPIRED_ERROR,
} from '../../../constants';
import {
    actions as bookingActions,
    selectors as bookingSelectors,
} from '../../appstate';
import { fetchFormAndSummaryAction } from '../fetchFormAndSummary';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';

const logger = Logger.getLogger('wanderu-app-booking:fetchTripBookingInfo');

function fetchCachedTripLiveCheck(tokenMap = {}, selectedFaresMap = {}) {
    return async (dispatch, getState) => {
        const tripKeysList = Object.entries(tokenMap);

        if (!tripKeysList.length) {
            throw new Error('no trip keys');
        }

        return Promise.all(tripKeysList.map(async ([tripId, token]) => {
            await dispatch(fetchTripAction.fetchTrip(tripId));

            const error = fetchTripAction.selectError(getState(), tripId);

            if (error) {
                return Promise.reject(error);
            }

            const tripData = tripSelectors.selectTripData(getState(), tripId);

            // `fetchCarrierAction.fetchCarrier` needs to happen first so that `liveCheckCachedTripAction.liveCheckCachedTrip` has the carrier preferred currency
            await dispatch(fetchCarrierAction.fetchCarrier(tripData.carrier));

            const seatInfo = await dispatch(liveCheckCachedTripAction.liveCheckCachedTrip(tripId, {
                eventMeta: {
                    itineraryId: bookingSelectors.selectItineraryId(getState()),
                    lastScraped: tripData.created,
                    originalPricing: tripData.pricing,
                },
                lang: languageSelector(getState()),
                selectedFare: selectedFaresMap[tripId],
                token,
            })) || {};

            if (seatInfo) {
                dispatch(bookingActions.setSeatInfo(seatInfo));
            }

            return undefined;
        }));
    };
}

function fetchTripBookingInfo(tokenMap, selectedFaresMap) {
    return async (dispatch, getState) => {
        const working = selectors.selectWorking(getState());
        const success = selectors.selectSuccess(getState());
        if (working || success) {
            return undefined;
        }

        dispatch(actions.startWork());

        let error;
        let meta;
        try {
            await dispatch(fetchCachedTripLiveCheck(tokenMap, selectedFaresMap));

            const trips = bookingSelectors.selectTripLiveChecks(getState());

            if (!trips || !trips.filter(Boolean).length) {
                throw new Error('no trips');
            }

            const selectedFares = trips.reduce((obj, { tripData }) => {
                if (!tripData) {
                    throw new Error('no trip data');
                }

                const {
                    carrier: carrierCode,
                    trip_id: tripId,
                    selected_fare: selectedFare = '',
                } = tripData;

                const shouldFetchSupplemental = carrierSelectors.selectRequiresSupplementalTripInfo(
                    getState(),
                    carrierCode,
                );

                if (shouldFetchSupplemental) {
                    return {
                        ...obj,
                        [tripId]: selectedFare,
                    };
                }

                return obj;
            }, {});

            const tripIds = Object.keys(selectedFares);

            if (tripIds.length) {
                return await dispatch(fetchTripInformationAction.fetchTripInformation(
                    tripIds,
                    selectedFares,
                ));
            }

            return undefined;
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            if (
                [
                    ...LIVE_CHECK_TRIP_NOT_FOUND_ERRORS,
                    LIVE_CHECK_CACHE_EXPIRED_ERROR,
                ].includes(ex.code)
            ) {
                logger.info('fetch', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                    tokenMap,
                });
            } else {
                logger.error('fetch', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                    tokenMap,
                });
            }

            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };

            return undefined;
        } finally {
            if (!error) {
                dispatch(fetchFormAndSummaryAction.fetch());
            }

            dispatch(actions.endWork(meta, error));
        }
    };
}

const fetchTripBookingInfoAction = {
    fetch: fetchTripBookingInfo,
    ...selectors,
    ...actions,
};

export {
    fetchTripBookingInfoAction,
};

const fetchTripBookingInfoSlice = {
    [namespace]: reducer,
};

export default fetchTripBookingInfoSlice;
